import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PlusCircle, Crown, Copy, Users, Sparkles } from "lucide-react";
import AmbassadorCertificate from "../components/AmbassadorCertificate";
import { toast } from "sonner";

export default function Dashboard() {
  const { user, refresh: refreshAuth } = useAuth();
  const [pledges, setPledges] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refStats, setRefStats] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/pledges/me").then((r) => setPledges(r.data)),
      api.get("/pledges/stats").then((r) => setStats(r.data)),
      api.get("/referrals/me").then((r) => setRefStats(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const totalMine = pledges.reduce((s, p) => s + Number(p.amount || 0), 0);

  const copyCode = () => {
    if (!refStats?.referral_code) return;
    navigator.clipboard.writeText(refStats.referral_code);
    toast.success("Ambassador code copied");
  };
  const copyLink = () => {
    if (!refStats?.referral_code) return;
    const url = `${window.location.origin}/pledge?ref=${refStats.referral_code}`;
    navigator.clipboard.writeText(url);
    toast.success("Share link copied");
  };
  const togglePublic = async () => {
    const next = !refStats.ambassador_public;
    try {
      await api.patch("/referrals/me/public", { ambassador_public: next });
      setRefStats((s) => ({ ...s, ambassador_public: next }));
      if (refreshAuth) refreshAuth();
      toast.success(next ? "You're on the public Ambassadors wall" : "Removed from public wall");
    } catch (_e) { toast.error("Could not update"); }
  };

  return (
    <div className="grain min-h-screen max-w-6xl mx-auto px-6 sm:px-10 pt-16 pb-24" data-testid="dashboard-page">
      <div className="label-eyebrow">Member Dashboard</div>
      <div className="mt-4 flex flex-wrap justify-between items-end gap-6">
        <div>
          <h1 className="font-serif text-5xl text-bdu-cream tracking-tight">Welcome, <span className="text-bdu-gold italic">{user?.name}</span></h1>
          {user?.is_member && (
            <span
              className="mt-3 inline-flex items-center gap-1.5 border border-bdu-gold/50 bg-bdu-gold/10 px-3 py-1 text-[10px] uppercase tracking-[0.25em] text-bdu-gold"
              data-testid="dashboard-founding-member-badge"
              title="Founding Member — paid $300 membership"
            >
              <Crown size={11} /> Founding Member
            </span>
          )}
        </div>
        <div className="flex gap-3">
          <Link to="/pledge" className="btn-gold" data-testid="dashboard-new-pledge-btn"><PlusCircle size={16}/> New Pledge</Link>
          <Link to="/submit-business" className="btn-ghost" data-testid="dashboard-submit-business-btn">Submit Business</Link>
        </div>
      </div>

      <div className="mt-14 grid md:grid-cols-3 gap-6">
        <StatCard label="Your Pledged Total" value={`$${totalMine.toLocaleString()}`} testId="my-total" />
        <StatCard label="Your Pledges" value={pledges.length} testId="my-count" />
        <StatCard label="Fund Total Raised" value={`$${(stats?.total_pledged || 0).toLocaleString()}`} testId="fund-total" />
      </div>

      {/* ==================== AMBASSADOR PROGRAM ==================== */}
      {refStats && (
        <section className="mt-16" data-testid="ambassador-section">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="label-eyebrow">Ambassador Program</div>
              <h2 className="mt-2 font-serif text-3xl text-bdu-cream">
                Turn your circle into a movement.
              </h2>
            </div>
            {refStats.current_tier && (
              <span className="inline-flex items-center gap-2 border border-bdu-gold/50 bg-bdu-gold/10 px-4 py-2 text-xs uppercase tracking-[0.2em] text-bdu-gold" data-testid="ambassador-tier-badge">
                <Sparkles size={13} /> {refStats.current_tier.name}
              </span>
            )}
          </div>

          {/* Share tools */}
          <div className="mt-8 grid md:grid-cols-3 gap-4">
            <div className="border border-bdu-gold/20 bg-bdu-surface p-5" data-testid="ambassador-code-card">
              <div className="label-eyebrow" style={{ fontSize: "0.6rem" }}>Your Ambassador Code</div>
              <div className="mt-3 flex items-center gap-3">
                <div className="font-mono text-2xl tracking-[0.3em] text-bdu-gold" data-testid="ambassador-code">
                  {refStats.referral_code}
                </div>
                <button onClick={copyCode} className="text-bdu-cream/60 hover:text-bdu-gold" data-testid="ambassador-copy-code" title="Copy code"><Copy size={14} /></button>
              </div>
              <button onClick={copyLink} className="mt-4 btn-ghost text-xs w-full justify-center" data-testid="ambassador-copy-link">
                <Copy size={12} /> Copy Share Link
              </button>
              <p className="mt-3 text-[10px] text-bdu-cream/40">
                Every donation using your code counts toward your tier.
              </p>
            </div>

            <div className="border border-bdu-gold/20 bg-bdu-surface p-5" data-testid="ambassador-progress-card">
              <div className="label-eyebrow" style={{ fontSize: "0.6rem" }}>Raised Through You</div>
              <div className="mt-3 font-serif text-3xl text-bdu-cream tabular-nums">
                ${Number(refStats.total_referred_dollars).toLocaleString()}
              </div>
              <div className="mt-1 text-xs text-bdu-cream/50">
                {refStats.referral_count} referred donation{refStats.referral_count === 1 ? "" : "s"}
              </div>
              {refStats.next_tier && (
                <div className="mt-4">
                  <div className="h-1.5 bg-bdu-gold/10 relative overflow-hidden">
                    <div
                      className="absolute inset-y-0 left-0 bg-gradient-to-r from-bdu-gold/70 to-bdu-gold transition-all"
                      style={{
                        width: `${Math.min(100, Math.round((refStats.total_referred_dollars / refStats.next_tier.min) * 100))}%`,
                      }}
                    />
                  </div>
                  <div className="mt-2 text-[11px] text-bdu-cream/60">
                    <span className="text-bdu-gold">${Number(refStats.dollars_to_next).toLocaleString()}</span> more to unlock{" "}
                    <span className="text-bdu-cream">{refStats.next_tier.name}</span>
                  </div>
                </div>
              )}
            </div>

            <div className="border border-bdu-gold/20 bg-bdu-surface p-5" data-testid="ambassador-public-card">
              <div className="label-eyebrow flex items-center gap-2" style={{ fontSize: "0.6rem" }}>
                <Users size={12} /> Public Ambassadors Wall
              </div>
              <p className="mt-3 text-xs text-bdu-cream/60 leading-relaxed">
                Opt in and your name + tier appear on the public Ambassadors leaderboard.
                Adds social pressure that makes people compete.
              </p>
              <button
                onClick={togglePublic}
                className={`mt-4 w-full text-xs uppercase tracking-widest py-2 border transition-colors ${
                  refStats.ambassador_public
                    ? "bg-bdu-gold text-bdu-black border-bdu-gold"
                    : "border-bdu-gold/30 text-bdu-cream hover:border-bdu-gold hover:text-bdu-gold"
                }`}
                data-testid="ambassador-public-toggle"
              >
                {refStats.ambassador_public ? "You're Public · Click to Hide" : "Show Me on the Wall"}
              </button>
            </div>
          </div>

          {/* Certificate */}
          <div className="mt-10">
            <AmbassadorCertificate
              name={user?.name}
              tier={refStats.current_tier}
              dollars={refStats.total_referred_dollars}
              code={refStats.referral_code}
            />
          </div>

          {/* Tier ladder */}
          <div className="mt-10 border border-bdu-gold/15 p-6" data-testid="tier-ladder">
            <div className="label-eyebrow">Ambassador Tiers</div>
            <div className="mt-5 grid sm:grid-cols-5 gap-3">
              {[
                {key:"bronze",name:"Bronze",min:1000,discount:10},
                {key:"silver",name:"Silver",min:5000,discount:15},
                {key:"gold",name:"Gold",min:25000,discount:20},
                {key:"platinum",name:"Platinum",min:100000,discount:30},
                {key:"founding",name:"Founding",min:500000,discount:100},
              ].map((t) => {
                const unlocked = refStats.total_referred_dollars >= t.min;
                return (
                  <div key={t.key} className={`p-4 border transition-all ${unlocked ? "border-bdu-gold/60 bg-bdu-gold/5" : "border-bdu-gold/10 bg-transparent"}`} data-testid={`tier-${t.key}`}>
                    <div className={`text-xs uppercase tracking-[0.2em] ${unlocked ? "text-bdu-gold" : "text-bdu-cream/30"}`}>{t.name}</div>
                    <div className={`mt-2 font-serif text-xl ${unlocked ? "text-bdu-cream" : "text-bdu-cream/40"}`}>${t.min.toLocaleString()}</div>
                    <div className={`mt-1 text-[10px] ${unlocked ? "text-bdu-cream/60" : "text-bdu-cream/30"}`}>{t.discount === 100 ? "Free merch" : `${t.discount}% off merch`}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Referred pledges */}
          {refStats.referred_pledges?.length > 0 && (
            <div className="mt-10 border border-bdu-gold/10" data-testid="referred-list">
              <div className="p-5 border-b border-bdu-gold/10 label-eyebrow">People You Brought In</div>
              <table className="w-full text-left text-sm">
                <thead className="bg-bdu-surface2 text-bdu-cream/60 uppercase tracking-widest text-xs">
                  <tr><th className="px-6 py-3">Donor</th><th className="px-6 py-3">Amount</th><th className="px-6 py-3">Date</th></tr>
                </thead>
                <tbody>
                  {refStats.referred_pledges.map((r) => (
                    <tr key={r.id} className="border-t border-bdu-gold/10">
                      <td className="px-6 py-3 text-bdu-cream/80">{r.donor_name}</td>
                      <td className="px-6 py-3 text-bdu-gold font-serif">${Number(r.amount).toLocaleString()}</td>
                      <td className="px-6 py-3 text-bdu-cream/50">{new Date(r.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
      {/* ==================== END AMBASSADOR ==================== */}

      <div className="mt-16">
        <h2 className="font-serif text-3xl text-bdu-cream">Your Pledge History</h2>
        <div className="mt-6 divider-gold" />
        {loading ? (
          <div className="text-bdu-cream/50 py-12 text-center">Loading...</div>
        ) : pledges.length === 0 ? (
          <div className="mt-8 bdu-card text-center py-14" data-testid="dashboard-empty">
            <div className="font-serif text-2xl text-bdu-cream">You haven't pledged yet.</div>
            <p className="mt-3 text-bdu-cream/60">Every dollar matters. Start with any amount.</p>
            <Link to="/pledge" className="btn-gold mt-8 inline-flex">Make Your First Pledge</Link>
          </div>
        ) : (
          <div className="mt-8 border border-bdu-gold/10">
            <table className="w-full text-left text-sm">
              <thead className="bg-bdu-surface2 text-bdu-cream/60 uppercase tracking-widest text-xs">
                <tr>
                  <th className="px-6 py-4">Date</th>
                  <th className="px-6 py-4">Amount</th>
                  <th className="px-6 py-4">Message</th>
                </tr>
              </thead>
              <tbody>
                {pledges.map((p) => (
                  <tr key={p.id} className="border-t border-bdu-gold/10" data-testid={`pledge-row-${p.id}`}>
                    <td className="px-6 py-4 text-bdu-cream/70">{new Date(p.created_at).toLocaleDateString()}</td>
                    <td className="px-6 py-4 text-bdu-gold font-serif text-lg">${Number(p.amount).toLocaleString()}</td>
                    <td className="px-6 py-4 text-bdu-cream/60">{p.note || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, testId }) {
  return (
    <div className="bdu-card" data-testid={`stat-${testId}`}>
      <div className="label-eyebrow">{label}</div>
      <div className="mt-4 font-serif text-4xl text-bdu-cream">{value}</div>
    </div>
  );
}
